<script setup>
/**
 * @description 移动端底部抽屉弹窗组件
 * @reason 统一移动端交互规范：上圆角 12px，内边距 16px，支持标题插槽及灵活的按钮配置
 * @param {boolean} visible - 是否显示弹窗
 * @param {string} [title] - 弹窗标题（优先使用插槽）
 * @param {boolean} [showConfirm=false] - 是否显示确认按钮
 * @param {boolean} [showCancel=false] - 是否显示取消按钮
 * @param {string} [confirmText='确认'] - 确认按钮文字
 * @param {string} [cancelText='取消'] - 取消按钮文字
 * @param {string|number} [height='auto'] - 弹窗高度
 * @param {string} [backgroundColor='#fff'] - 弹窗整体背景色，默认白色
 * @param {string} [footerBackgroundColor] - footer 区域背景色，不设置则跟随 backgroundColor
 * @param {boolean} [hideHeader=false] - 是否隐藏默认头部
 * @param {string|string[]|Object} [panelClass] - 透传到抽屉容器的类名
 * @emits update:visible - 更新显示状态
 * @emits confirm - 点击确认按钮
 * @emits cancel - 点击取消按钮
 * @emits close - 弹窗关闭
 * @slot default - 主体内容（溢出时仅此区域滚动）
 * @slot title - 自定义标题
 * @slot footer - 底部固定区域（固定在弹窗底部，不随内容滚动）
 */

import { computed } from 'vue'
import { IconClose } from '@arco-design/web-vue/es/icon'
import { THEME_LAYOUT } from '~/utils/theme'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  showConfirm: {
    type: Boolean,
    default: false,
  },
  showCancel: {
    type: Boolean,
    default: false,
  },
  confirmText: {
    type: String,
    default: '确认',
  },
  cancelText: {
    type: String,
    default: '取消',
  },
  height: {
    type: [String, Number],
    default: 'auto',
  },
  backgroundColor: {
    type: String,
    default: '#fff',
  },
  hideHeader: {
    type: Boolean,
    default: false,
  },
  panelClass: {
    type: [String, Array, Object],
    default: '',
  },
  footerBackgroundColor: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:visible', 'confirm', 'cancel', 'close'])

const isAutoHeight = computed(() => props.height === 'auto')

const drawerBodyStyle = computed(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: isAutoHeight.value ? 'auto' : '100%',
  maxHeight: isAutoHeight.value ? `calc(100vh - ${THEME_LAYOUT.headerHeightMobile}px)` : 'none',
  minHeight: 0,
  overflow: 'hidden',
  padding: 0,
}))

const drawerStyle = computed(() => ({
  borderRadius: '12px 12px 0 0',
  backgroundColor: props.backgroundColor,
}))

const footerStyle = computed(() => {
  if (!props.footerBackgroundColor) return {}
  return { backgroundColor: props.footerBackgroundColor }
})

const handleCancel = () => {
  emit('update:visible', false)
  emit('cancel')
  emit('close')
}

const handleConfirm = () => {
  emit('confirm')
}

const handleClose = () => {
  emit('update:visible', false)
  emit('close')
}
</script>

<template>
  <a-drawer
    :visible="visible"
    placement="bottom"
    :footer="false"
    :header="false"
    :closable="false"
    :mask-closable="true"
    :esc-to-close="true"
    unmount-on-close
    :height="height === 'auto' ? 'auto' : height"
    :class="['base-bottom-drawer', panelClass]"
    :drawer-style="drawerStyle"
    :body-style="drawerBodyStyle"
    @cancel="handleClose"
  >
    <div class="base-bottom-drawer__container">
      <div
        v-if="!hideHeader"
        class="base-bottom-drawer__header"
        :class="{ 'base-bottom-drawer__header--no-title': !title && !$slots.title }"
      >
        <div class="header-action header-action--left">
          <button
            v-if="showConfirm && showCancel"
            class="action-btn action-btn--cancel"
            @click="handleCancel"
          >
            {{ cancelText }}
          </button>
        </div>

        <div class="header-title">
          <slot name="title">{{ title }}</slot>
        </div>

        <div class="header-action header-action--right">
          <button v-if="showConfirm" class="action-btn action-btn--confirm" @click="handleConfirm">
            {{ confirmText }}
          </button>
          <button v-else class="action-btn action-btn--close" @click="handleClose">
            <icon-close />
          </button>
        </div>
      </div>

      <div
        class="base-bottom-drawer__content"
        :class="{ 'base-bottom-drawer__content--no-footer': !$slots.footer }"
      >
        <slot />
      </div>

      <div v-if="$slots.footer" class="base-bottom-drawer__footer" :style="footerStyle">
        <slot name="footer" />
      </div>
    </div>
  </a-drawer>
</template>

<style lang="scss" scoped>
.base-bottom-drawer {
  &__container {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
  }

  &__header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 48px;
    padding: 16px;
    position: relative;

    &--no-title {
      min-height: 24px;
    }

    .header-title {
      flex: 1;
      text-align: center;
      font-size: 18px;
      font-weight: 500;
      line-height: 26px;
      color: $text-title-color;
      @include text-ellipsis(1);
    }

    .header-action {
      min-width: 60px;
      display: flex;
      align-items: center;

      &--left {
        justify-content: flex-start;
      }

      &--right {
        justify-content: flex-end;
      }

      .action-btn {
        background: none;
        border: none;
        padding: 0;
        font-size: 16px;
        cursor: pointer;
        outline: none;

        &--cancel {
          color: $text-subtitle-color;
        }

        &--confirm {
          color: $primary-color;
          font-weight: 500;
        }

        &--close {
          font-size: 20px;
          color: $text-subtitle-color;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        &:active {
          opacity: 0.7;
        }
      }
    }
  }

  &__content {
    flex: 1;
    min-height: 0;
    padding: 0 16px 16px;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }

  &__footer {
    flex-shrink: 0;
    padding: 12px 16px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
    border-top: 1px solid $border-dashed-color;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
  }
}
</style>

<style lang="scss">
.arco-drawer-container.base-bottom-drawer {
  z-index: 1300 !important;
}
</style>
